import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router'
import { User, Lock, Bell, Palette } from 'lucide-react'
import { motion, type Variants } from 'framer-motion'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

const pageVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
}

const SETTINGS_TABS = [
  { to: '/settings/profile', segment: 'profile', label: 'Profile', icon: User },
  { to: '/settings/security', segment: 'security', label: 'Security', icon: Lock },
  { to: '/settings/notifications', segment: 'notifications', label: 'Notifications', icon: Bell },
  { to: '/settings/appearance', segment: 'appearance', label: 'Appearance', icon: Palette },
] as const

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsLayout,
})

function SettingsLayout() {
  const { t } = useTranslation()
  const matches = useMatches()
  const activeTab = matches[matches.length - 1]?.routeId?.split('/').pop() ?? 'profile'

  return (
    <motion.div variants={pageVariants} initial="hidden" animate="visible" className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('common:navigation.settings')}</h1>
        <p className="text-muted-foreground mt-1">{t('settings:manageYourAccountSecurityAndPreferences')}</p>
      </div>

      <div className="flex gap-1 border-b">
        {SETTINGS_TABS.map(tab => (
          <Link
            key={tab.to}
            to={tab.to}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.segment
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </Link>
        ))}
      </div>

      <Outlet />
    </motion.div>
  )
}
